import React from "react";
import "./PrivacyPolicy.css";

function PrivacyPolicy() {
  return (
    <div className="privacy-policy">
      <div className="privacy-policy-content">
        <h1>Privacy Policy – EffiGen LLC</h1>
        <p className="effective-date">
          <strong>Effective Date:</strong> Sep 28, 2025
        </p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            At EffiGen LLC ("EffiGen," "we," "us," or "our"), we are committed
            to protecting your privacy and handling your personal information
            with care and transparency. This Privacy Policy explains what
            information we collect, how we use it, and the rights you have
            regarding your data. This policy applies to our website, consulting
            services, AI products, training programs, and all related services.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <p>
            We collect information that you provide directly to us and
            information that is automatically collected when you use our
            services:
          </p>

          <h3>Personal Information:</h3>
          <ul>
            <li>Name, email address, phone number</li>
            <li>Professional title</li>
          </ul>

          <h3>Business Information:</h3>
          <ul>
            <li>Company name, size, and industry</li>
            <li>Role and department within your organization</li>
            <li>Business challenges, goals, and project requirements</li>
            <li>
              Information shared during consultations, assessments, and
              workshops
            </li>
          </ul>

          <h3>Technical Information:</h3>
          <ul>
            <li>
              We collect only information that is applicable to the service or
              product we are providing
            </li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect for the following purposes:</p>
          <ul>
            <li>
              <strong>Service Delivery:</strong> To provide AI consulting,
              develop custom products, conduct workshops and assessments, and
              deliver training programs
            </li>
            <li>
              <strong>Communication:</strong> To respond to inquiries, schedule
              consultations, send requested resources, and provide customer
              support
            </li>
            <li>
              <strong>Service Improvement:</strong> To analyze usage patterns,
              identify areas for enhancement, and develop new offerings that
              better meet client needs
            </li>
            <li>
              <strong>Marketing:</strong> With your consent, we may use case
              studies, industry insights, and information about our services
              (you may opt out at any time)
            </li>
            <li>
              <strong>Business Operations:</strong> To process payments,
              maintain records, fulfill contractual obligations, and manage
              client relationships
            </li>
            <li>
              <strong>Legal Compliance:</strong> To comply with applicable laws,
              regulations, and legal processes
            </li>
          </ul>
        </section>

        <section>
          <h2>4. How We Share Information</h2>
          <p>
            We respect your privacy and limit how we share your information:
          </p>

          <h3>Service Providers:</h3>
          <p>
            We may share your information with trusted third-party vendors who
            assist with website hosting, email delivery, payment processing,
            analytics, and other operational functions. These providers are
            contractually obligated to maintain confidentiality and use your
            information only to perform services on our behalf. All such uses
            are properly defined in our contracts prior to the commencement of
            the service or product.
          </p>

          <h3>Legal Requirements:</h3>
          <p>
            We may disclose information when required by law, court order, or
            legal process, or when necessary to protect our rights, property,
            safety, or that of our clients or others.
          </p>

          <h3>Business Transfers:</h3>
          <p>
            In the event of a merger, acquisition, or sale of assets, your
            information may be transferred to the successor organization.
          </p>

          <h3>Your Consent:</h3>
          <p>
            We may share information in other circumstances with your explicit
            consent.
          </p>

          <div className="highlight-box">
            <p>
              <strong>We Never Sell Your Data:</strong> EffiGen does not sell,
              rent, or trade your personal information to third parties for
              their marketing purposes.
            </p>
          </div>
        </section>

        <section>
          <h2>5. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to
            fulfill the purposes outlined in this policy, unless a longer
            retention period is required or permitted by law. The specific
            retention period depends on:
          </p>
          <ul>
            <li>The nature of the information and why we collected it</li>
            <li>
              Whether you are an active client or have an ongoing relationship
              with us
            </li>
            <li>
              Legal, regulatory, or contractual obligations that require us to
              retain certain records
            </li>
            <li>
              The need to resolve disputes, enforce agreements, or protect our
              legal rights
            </li>
          </ul>
          <p>
            When information is no longer needed, we securely delete or
            anonymize it in accordance with our data retention schedule and
            applicable legal requirements.
          </p>
        </section>

        <section>
          <h2>6. Your Rights</h2>
          <p>
            You have the following rights regarding your personal information:
          </p>
          <ul>
            <li>
              <strong>Access:</strong> Request a copy of the personal data we
              hold about you
            </li>
            <li>
              <strong>Correction:</strong> Update or correct inaccurate or
              incomplete information
            </li>
            <li>
              <strong>Deletion:</strong> Request deletion of your personal data,
              subject to legal or contractual obligations
            </li>
            <li>
              <strong>Opt-Out:</strong> Unsubscribe from marketing
              communications at any time using the link in our emails or by
              contacting us directly
            </li>
            <li>
              <strong>Data Portability:</strong> Request a copy of your data in
              a structured, commonly used format
            </li>
            <li>
              <strong>Objection:</strong> Object to certain processing
              activities, such as direct marketing
            </li>
            <li>
              <strong>Transparency:</strong> Request information about how we
              process your data and who we share it with
            </li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the
            information in Section 12. We will respond to your request within a
            reasonable timeframe as required by applicable law.
          </p>
        </section>

        <section>
          <h2>7. Security Measures</h2>
          <p>
            We implement reasonable technical, administrative, and physical
            safeguards designed to protect your personal information from
            unauthorized access, disclosure, alteration, and destruction. Our
            security measures include:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Access controls and authentication protocols</li>
            <li>Regular security assessments and updates</li>
            <li>Employee training on data protection and confidentiality</li>
            <li>Secure backup and disaster recovery procedures</li>
          </ul>
          <p>
            While we strive to protect your information, no method of
            transmission or storage is completely secure. We cannot guarantee
            absolute security but remain committed to continuously improving our
            practices.
          </p>
        </section>

        <section>
          <h2>8. International Data Transfers</h2>
          <p>
            EffiGen is based in the United States. If you are accessing our
            services from outside the U.S., please be aware that your
            information may be transferred to, stored, and processed in the
            United States or other countries where our service providers
            operate. These countries may have data protection laws that differ
            from those in your jurisdiction.
          </p>
          <p>
            Unless our clients reside outside the United States, we don't
            transfer personal data internationally.
          </p>
          <p>
            When we transfer personal data internationally, we implement
            appropriate safeguards such as standard contractual clauses,
            adequacy decisions, or other legally approved mechanisms to ensure
            your information receives adequate protection.
          </p>
        </section>

        <section>
          <h2>9. Cookies and Tracking Technologies</h2>
          <p>
            Our website uses cookies and similar technologies to enhance your
            experience, analyze usage, and support marketing efforts. Cookies
            are small text files stored on your device that help us recognize
            you and remember your preferences.
          </p>

          <h3>Types of Cookies We Use:</h3>
          <ul>
            <li>
              <strong>Essential Cookies:</strong> Necessary for website
              functionality and security
            </li>
            <li>
              <strong>Analytics Cookies:</strong> Help us understand how
              visitors use our site
            </li>
            <li>
              <strong>Functionality Cookies:</strong> Remember your preferences
              and settings
            </li>
            <li>
              <strong>Marketing Cookies:</strong> Deliver relevant content and
              measure campaign effectiveness
            </li>
          </ul>

          <p>
            You can manage cookie preferences through your browser settings.
            Most browsers allow you to block or delete cookies, though this may
            affect website functionality. To learn more about cookies and how to
            control them, visit{" "}
            <a
              href="http://www.allaboutcookies.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.allaboutcookies.org
            </a>
            .
          </p>
        </section>

        <section>
          <h2>10. Children's Privacy</h2>
          <p>
            Our services are designed for businesses and professionals. We do
            not knowingly collect personal information from children under the
            age of 16. If we become aware that we have inadvertently collected
            information from a child under 16, we will take steps to delete it
            promptly. If you believe we have collected information from a child,
            please contact us immediately.
          </p>
        </section>

        <section>
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in
            our practices, services, legal requirements, or other factors. When
            we make material changes, we will notify you by updating the
            "Effective Date" at the top of this policy and, where appropriate,
            provide additional notice through email or a prominent announcement
            on our website.
          </p>
          <p>
            We encourage you to review this policy regularly to stay informed
            about how we protect your information. Your continued use of our
            services after changes are posted constitutes acceptance of the
            updated policy.
          </p>
        </section>

        <section>
          <h2>12. Contact Information</h2>
          <p>
            If you have questions, concerns, or requests regarding this Privacy
            Policy or our data practices, please contact us:
          </p>

          <div className="contact-info">
            <p>
              <strong>EffiGen LLC</strong>
            </p>
            <p>
              Email:{" "}
              <a
                href="mailto:dennis.morozov@effigen.ai
"
              >
                dennis.morozov@effigen.ai
              </a>
            </p>
          </div>

          <p>
            We are committed to addressing your concerns and will respond to all
            inquiries in a timely manner.
          </p>
        </section>

        <div className="policy-footer">
          <p>
            <em>
              This Privacy Policy was last updated on Sep 28, 2025. By using
              EffiGen's services, you acknowledge that you have read and
              understood this policy.
            </em>
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
